import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Flame, Bookmark, Search, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StreakBadge } from "@/components/StreakBadge";
import {
  Eyebrow,
  HeroPhoto,
  Rule,
  StripePlaceholder,
  Wordmark,
} from "@/components/editorial";
import { PHOTOS } from "@/lib/photos";
import { useMenuPlan } from "@/hooks/useMenuPlan";
import { useNextWeekMenuPlan } from "@/hooks/useNextWeekMenuPlan";
import { useAuth } from "@/features/auth/AuthProvider";
import {
  formatDayLong,
  formatPrice,
  formatWeekRange,
  parseISODate,
} from "@/features/menu/menuLabels";
import {
  getIsoWeekNumber,
  getNextWeekStart,
  getParisWeekday,
} from "@/features/menu/themePreference";
import { NextWeekPrepSheet } from "@/features/menu/NextWeekPrepSheet";
import { useMenu } from "@/features/menu/store";
import {
  ReadOnlyBanner,
  useEntitlements,
  usePaywall,
} from "@/features/paywall";

/**
 * Mobile dashboard — see /tmp/design (mobile-app.jsx Dashboard).
 */
export function DashboardPage() {
  const { t } = useTranslation();
  const { plan, isLoading, isPreview, isCurrentWeek } = useMenuPlan();
  const { plan: nextWeekPlan } = useNextWeekMenuPlan();
  const { user } = useAuth();
  const navigate = useNavigate();
  const pickedCount = useMenu((s) => s.pickedItems.length);
  const doneCount = useMenu((s) => s.doneSteps.length);
  const [prepSheetOpen, setPrepSheetOpen] = useState(false);
  const { isReadOnly } = useEntitlements();
  const { open: openPaywall } = usePaywall();

  // Europe/Paris weekday, same calendar as the backend generation window.
  const today = getParisWeekday(); // 0=Sun, 4=Thu, 5=Fri, 6=Sat
  const isGenerationWindow = today === 0 || today >= 4;

  // Hero label: the real ISO week of the plan, and whether it is this
  // week's menu, last week's, or the demo (was hard-coded "Semaine 19").
  const planWeek = plan?.week_start
    ? getIsoWeekNumber(String(plan.week_start))
    : null;
  const heroLabel =
    isPreview || planWeek === null
      ? t("dashboard.week_example")
      : isCurrentWeek
        ? t("dashboard.week_ready", { week: planWeek })
        : t("dashboard.week_past", { week: planWeek });

  const displayName =
    (user?.user_metadata?.display_name as string | undefined) ??
    user?.email?.split("@")[0] ??
    "Chef";
  const firstName = displayName.split(" ")[0];
  const todayName = new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date());

  const ingredientCount = plan?.shopping_list
    ? Object.values(plan.shopping_list.items_by_aisle).flat().filter(Boolean)
        .length
    : 0;
  const recipeCount = plan?.days.length ?? 0;
  const stepCount = plan?.cooking_plan.length ?? 0;

  const todayRecipe =
    plan?.days.find((d) => {
      const day = parseISODate(d.day);
      const t = new Date();
      return (
        day.getFullYear() === t.getFullYear() &&
        day.getMonth() === t.getMonth() &&
        day.getDate() === t.getDate()
      );
    })?.meals.dinner ?? plan?.days[0]?.meals.dinner;

  return (
    <div className="bg-background pb-6">
      {/* Header */}
      <header className="flex items-center justify-between px-5 pb-3 pt-4">
        <Wordmark size="sm" />
        <div className="flex gap-4 text-foreground">
          <button aria-label="Rechercher" className="hover:text-accent">
            <Search className="size-[18px]" strokeWidth={1.25} />
          </button>
          <button aria-label="Favoris" className="hover:text-accent">
            <Bookmark className="size-[18px]" strokeWidth={1.25} />
          </button>
        </div>
      </header>

      {/* Read-only access banner */}
      {isReadOnly && (
        <div className="px-5 pt-3">
          <ReadOnlyBanner
            onUpgrade={() => openPaywall({ variant: "read_only" })}
          />
        </div>
      )}

      {/* Greeting */}
      <section className="px-6 pb-6 pt-2">
        <Eyebrow className="mb-1.5 first-letter:uppercase">{todayName}</Eyebrow>
        <h1 className="h-display text-[36px] leading-[1.05]">
          Bonjour, <span className="italic">{firstName}</span>.
        </h1>
        <StreakBadge className="mt-3" />
      </section>

      {/* Hero card — this week */}
      <section className="px-5">
        <Link
          to="/menu"
          className="relative block min-h-[200px] overflow-hidden bg-foreground text-background"
        >
          <HeroPhoto src={PHOTOS.greens} blur={20} opacity={0.5} />
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.1), rgba(0,0,0,0.5))",
            }}
          />
          <div className="relative p-6">
            <Eyebrow accent className="mb-3.5">
              {heroLabel}
            </Eyebrow>
            <h2 className="font-serif text-[28px] font-normal leading-[1.1]">
              Sept dîners,
              <br />
              {plan?.theme ? (
                <span className="italic">{plan?.theme}.</span>
              ) : null}
            </h2>
            <div className="mt-5 flex gap-3.5 text-[12px] text-white/75">
              <span>{recipeCount} recettes</span>
              <span>·</span>
              <span>{ingredientCount} ingrédients</span>
              <span>·</span>
              <span>
                ≈{" "}
                {plan?.estimated_cost_eur != null
                  ? formatPrice(plan.estimated_cost_eur)
                  : "—"}
              </span>
            </div>
            <Button size="default" variant="cream" className="mt-5">
              Ouvrir le menu <ArrowRight />
            </Button>
          </div>
        </Link>
      </section>

      {/* Current-week catch-up — Mon–Wed without a menu for THIS week.
          useMenuPlan always returns something (demo or last week's menu),
          so "!plan" never held; key off isCurrentWeek instead. */}
      {!isCurrentWeek && !isLoading && !isGenerationWindow && (
        <section className="mt-4 px-5">
          {isReadOnly ? (
            <button
              type="button"
              onClick={() => openPaywall({ variant: "read_only" })}
              className="flex w-full items-center gap-3.5 border border-dashed border-ink-30 bg-ink-5 px-5 py-4 text-left transition-colors hover:bg-ink-10"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink-20 text-ink-50">
                <Sparkles className="size-[18px]" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <div className="font-serif text-[17px] italic text-ink-50">
                  Générer mon menu de la semaine
                </div>
                <div className="mt-0.5 text-[12px] text-ink-40">
                  Accès réservé aux abonnés
                </div>
              </div>
              <ArrowRight className="size-4 text-ink-40" />
            </button>
          ) : (
            <Link
              to="/menu?generate=true"
              className="flex w-full items-center gap-3.5 border border-dashed border-accent bg-accent/5 px-5 py-4 text-left transition-colors hover:bg-accent/10"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-background">
                <Sparkles className="size-[18px]" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <div className="font-serif text-[17px] italic">
                  Générer mon menu de la semaine
                </div>
                <div className="mt-0.5 text-[12px] text-ink-50">
                  La brigade prépare 7 dîners en ~60 s.
                </div>
              </div>
              <ArrowRight className="size-4 text-accent" />
            </Link>
          )}
        </section>
      )}

      {/* Next week — visible Thu–Sun */}
      {isGenerationWindow && (
        <section className="mt-4 px-5">
          {nextWeekPlan ? (
            <Link
              to="/menu/next"
              className="relative flex items-center gap-4 border border-hair bg-paper px-5 py-4 transition-colors hover:border-accent"
            >
              <div className="flex-1">
                <Eyebrow className="mb-1">Semaine prochaine</Eyebrow>
                <p className="font-serif text-[17px] italic">
                  {nextWeekPlan.theme ?? "Menu prêt"}
                </p>
                <p className="mt-0.5 text-[12px] capitalize text-ink-50">
                  {formatDayLong(getNextWeekStart())}
                </p>
              </div>
              <ArrowRight className="size-4 shrink-0 text-foreground" />
            </Link>
          ) : (
            <button
              type="button"
              onClick={() =>
                isReadOnly
                  ? openPaywall({ variant: "read_only" })
                  : setPrepSheetOpen(true)
              }
              className={
                isReadOnly
                  ? "flex w-full items-center gap-3.5 border border-dashed border-ink-30 bg-ink-5 px-5 py-4 text-left transition-colors hover:bg-ink-10"
                  : "flex w-full items-center gap-3.5 border border-dashed border-accent bg-accent/5 px-5 py-4 text-left transition-colors hover:bg-accent/10"
              }
            >
              <div
                className={
                  isReadOnly
                    ? "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink-20 text-ink-50"
                    : "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-background"
                }
              >
                <Sparkles className="size-[18px]" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <div
                  className={
                    isReadOnly
                      ? "font-serif text-[17px] italic text-ink-50"
                      : "font-serif text-[17px] italic"
                  }
                >
                  Préparer la semaine prochaine
                </div>
                <div className="mt-0.5 text-[12px] capitalize text-ink-50">
                  {isReadOnly
                    ? "Accès réservé aux abonnés"
                    : formatDayLong(getNextWeekStart())}
                </div>
              </div>
              <ArrowRight
                className={
                  isReadOnly ? "size-4 text-ink-40" : "size-4 text-accent"
                }
              />
            </button>
          )}
        </section>
      )}

      <NextWeekPrepSheet
        open={prepSheetOpen}
        onOpenChange={setPrepSheetOpen}
        onGenerate={() => navigate("/menu/next?generate=true")}
      />

      {/* Today's pick */}
      <section className="px-6 pb-6 pt-6">
        <div className="mb-3.5 flex items-baseline justify-between">
          <Eyebrow>Ce soir</Eyebrow>
          <Link to="/menu" className="text-[12px] text-accent">
            Voir tout →
          </Link>
        </div>
        {isLoading || !todayRecipe ? (
          <DashboardSkeleton />
        ) : (
          <Link
            to={`/recipe/${todayRecipe.slug}`}
            className="flex items-start gap-4"
          >
            <div className="h-[92px] w-[92px] shrink-0 overflow-hidden">
              <StripePlaceholder label={todayRecipe.slug.slice(0, 12)} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-2 text-[10.5px] uppercase tracking-[0.18em] text-accent">
                Base · 1
              </div>
              <h3 className="font-serif text-[21px] font-normal leading-[1.15] tracking-[-0.005em]">
                {todayRecipe.title}
              </h3>
              <div className="mt-2.5 flex gap-3 text-[12px] text-ink-50">
                <span>
                  {todayRecipe.prep_minutes + todayRecipe.cook_minutes} min
                </span>
                <span>·</span>
                <span>520 kcal</span>
                <span>·</span>
                <span>★ 4.8</span>
              </div>
            </div>
          </Link>
        )}
      </section>

      <Rule className="mx-6" />

      {/* Stats */}
      <section className="px-6 py-6">
        <Eyebrow className="mb-3.5">Votre dimanche</Eyebrow>
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            label="Courses"
            value={`${pickedCount}/${ingredientCount}`}
            sub="à acheter"
          />
          <StatCard
            label="Cuisine"
            value={`${doneCount}/${stepCount}`}
            sub="étapes prêtes"
          />
        </div>
      </section>

      {/* Quick action — start cooking */}
      <section className="px-6 pb-6">
        <Link
          to="/batch-overview"
          className="flex items-center gap-3.5 border border-hair bg-paper px-5 py-4 transition-colors hover:border-accent"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-background">
            <Flame className="size-[18px]" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="font-serif text-[17px] italic">Lancer le batch</div>
            <div className="text-[12px] text-ink-50">
              2 h 30 · {stepCount} étapes guidées
            </div>
          </div>
          <ArrowRight className="size-4 text-foreground" />
        </Link>
      </section>

      {/* This week list — quick peek */}
      {plan && (
        <section className="px-6 pb-10">
          <Eyebrow className="mb-3.5">Au menu</Eyebrow>
          <ul className="divide-y divide-hair border-y border-hair">
            {plan.days.slice(0, 4).map((d) => {
              const r = d.meals.dinner;
              if (!r) return null;
              return (
                <li key={d.day}>
                  <Link
                    to={`/recipe/${r.slug}`}
                    className="flex items-baseline justify-between gap-3 py-3"
                  >
                    <span className="font-serif text-[15px] leading-tight">
                      {r.title}
                    </span>
                    <span className="shrink-0 font-mono text-[11px] text-ink-50">
                      {formatDayChip(d.day)}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
          <Link to="/menu" className="mt-3 inline-flex text-[12px] text-accent">
            Voir les 7 dîners →
          </Link>
        </section>
      )}

      {plan?.week_start && (
        <p className="px-6 pb-6 text-center text-[11px] text-ink-30">
          {formatWeekRange(plan.week_start, plan.days.length)}
        </p>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="border border-hair bg-background px-5 py-4">
      <div className="mb-2.5 text-[10.5px] uppercase tracking-[0.18em] text-ink-50">
        {label}
      </div>
      <div className="font-serif text-[32px] font-normal tracking-[-0.01em]">
        {value}
      </div>
      <div className="mt-1 text-[12px] text-ink-50">{sub}</div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="flex items-start gap-4">
      <div className="h-[92px] w-[92px] shrink-0 bg-bone" />
      <div className="flex-1 space-y-2.5">
        <div className="h-3 w-24 bg-bone" />
        <div className="h-5 w-full bg-bone" />
        <div className="h-3 w-32 bg-bone" />
      </div>
    </div>
  );
}

function formatDayChip(iso: string): string {
  const d = parseISODate(iso);
  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "short",
    day: "numeric",
  })
    .format(d)
    .replace(".", "");
}
